import { Typography } from '@mui/material';
import {
  AutocompleteBase,
  Button,
  CircularProgress,
  Modal,
} from '@shared/components';
import {
  BraintrustEnvironment,
  useGetBraintrustDatasetsQuery,
} from '@shared/generated/graphql';
import { useState } from 'react';
import { NewDatasetInput } from './useSaveSnapshotToDataset';

export type { NewDatasetInput } from './useSaveSnapshotToDataset';

type DatasetOption = {
  id: string;
  label: string;
  description?: string;
};

type EnvironmentConfig = {
  value: BraintrustEnvironment;
  label: string;
};

const ENVIRONMENT_CONFIGS: EnvironmentConfig[] = [
  { value: BraintrustEnvironment.DEVELOPMENT, label: 'Development' },
  { value: BraintrustEnvironment.STAGING, label: 'Staging' },
  { value: BraintrustEnvironment.PRODUCTION, label: 'Production' },
  {
    value: BraintrustEnvironment.RELEASE_CANDIDATE,
    label: 'Release Candidate',
  },
];

export type SaveSnapshotToDatasetModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSave: (
    existingDatasetIds: string[],
    newDatasets: NewDatasetInput[]
  ) => Promise<void>;
};

type EnvironmentSelections = Record<
  BraintrustEnvironment,
  (DatasetOption | string)[]
>;

const createEmptySelections = (): EnvironmentSelections => ({
  [BraintrustEnvironment.DEVELOPMENT]: [],
  [BraintrustEnvironment.STAGING]: [],
  [BraintrustEnvironment.PRODUCTION]: [],
  [BraintrustEnvironment.RELEASE_CANDIDATE]: [],
});

export function SaveSnapshotToDatasetModal({
  isOpen,
  setIsOpen,
  onSave,
}: SaveSnapshotToDatasetModalProps) {
  if (!isOpen) return null;
  return (
    <SaveSnapshotToDatasetModalContent setIsOpen={setIsOpen} onSave={onSave} />
  );
}

type SaveSnapshotToDatasetModalContentProps = Omit<
  SaveSnapshotToDatasetModalProps,
  'isOpen'
>;

function SaveSnapshotToDatasetModalContent({
  setIsOpen,
  onSave,
}: SaveSnapshotToDatasetModalContentProps) {
  const [selections, setSelections] = useState<EnvironmentSelections>(
    createEmptySelections()
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleSelectionChange = (
    environment: BraintrustEnvironment,
    newSelection: (DatasetOption | string)[]
  ) => {
    setSelections((prev) => ({
      ...prev,
      [environment]: newSelection,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    const existingDatasetIds: string[] = [];
    const newDatasets: NewDatasetInput[] = [];

    for (const config of ENVIRONMENT_CONFIGS) {
      const environmentSelections = selections[config.value];
      for (const item of environmentSelections) {
        if (typeof item === 'string') {
          newDatasets.push({ name: item, environment: config.value });
        } else {
          existingDatasetIds.push(item.id);
        }
      }
    }

    try {
      await onSave(existingDatasetIds, newDatasets);
      setIsOpen(false);
      setSelections(createEmptySelections());
    } catch {
      // NOTE(cursor): Keep the modal open on failure so the user can retry.
      // The caller is expected to surface the error (e.g. via a toast).
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const hasAnySelection = Object.values(selections).some(
    (sel) => sel.length > 0
  );

  return (
    <Modal.Root open>
      <Modal.Content className="!overflow-visible" onKeyDown={handleKeyDown}>
        <Modal.Header
          title="Save snapshot to dataset"
          hasCloseButton
          onClose={handleClose}
          className="px-6"
        />
        <div className="px-6 pb-6">
          <div className="mb-4 flex flex-col gap-4">
            {ENVIRONMENT_CONFIGS.map((config) => (
              <EnvironmentDatasetSelector
                key={config.value}
                environment={config.value}
                label={config.label}
                selectedDatasets={selections[config.value]}
                onSelectionChange={(newSelection) =>
                  handleSelectionChange(config.value, newSelection)
                }
              />
            ))}
          </div>
          <FormActions
            isSaving={isSaving}
            isDisabled={!hasAnySelection}
            onSave={handleSave}
            onClose={handleClose}
          />
        </div>
      </Modal.Content>
    </Modal.Root>
  );
}

type EnvironmentDatasetSelectorProps = {
  environment: BraintrustEnvironment;
  label: string;
  selectedDatasets: (DatasetOption | string)[];
  onSelectionChange: (datasets: (DatasetOption | string)[]) => void;
};

function EnvironmentDatasetSelector({
  environment,
  label,
  selectedDatasets,
  onSelectionChange,
}: EnvironmentDatasetSelectorProps) {
  const { data, loading } = useGetBraintrustDatasetsQuery({
    variables: { input: { environment } },
  });

  const datasetOptions: DatasetOption[] =
    data?.getBraintrustDatasets.map((dataset) => ({
      id: dataset.id,
      label: dataset.name,
      ...(dataset.description && { description: dataset.description }),
    })) ?? [];

  const optionsByLabel = new Map(datasetOptions.map((d) => [d.label, d]));

  const newDatasetNames = selectedDatasets
    .filter((item): item is string => typeof item === 'string')
    .filter((name) => !optionsByLabel.has(name));

  const handleChange = (value: (DatasetOption | string)[]) => {
    const normalized = value.map((item) =>
      typeof item === 'string' ? optionsByLabel.get(item) ?? item : item
    );
    onSelectionChange(normalized);
  };

  return (
    <div>
      <Typography variant="body2" className="mb-1 text-[10px] text-gray-500">
        {label}
      </Typography>
      {loading ? (
        <div className="flex h-10 items-center justify-center">
          <CircularProgress size="xs" />
        </div>
      ) : (
        <>
          <AutocompleteBase
            multiple
            freeSolo
            submitOnBlur
            options={datasetOptions}
            value={selectedDatasets}
            onChange={(_, value) => {
              handleChange(value);
            }}
            placeholder="Type to create or select..."
            size="small"
            blurOnSelect={false}
          />
          <NewDatasetsList names={newDatasetNames} />
        </>
      )}
    </div>
  );
}

type NewDatasetsListProps = {
  names: string[];
};

function NewDatasetsList({ names }: NewDatasetsListProps) {
  if (names.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 space-y-0.5">
      {names.map((name) => (
        <Typography
          key={name}
          variant="body2"
          className="text-[10px] italic text-gray-600"
        >
          Creating new dataset &apos;{name}&apos;
        </Typography>
      ))}
    </div>
  );
}

type FormActionsProps = {
  isSaving: boolean;
  isDisabled: boolean;
  onSave: () => void;
  onClose: () => void;
};

function FormActions({
  isSaving,
  isDisabled,
  onSave,
  onClose,
}: FormActionsProps) {
  return (
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onPress={onClose} isDisabled={isSaving}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onPress={onSave}
        isDisabled={isSaving || isDisabled}
      >
        {isSaving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );
}
